import React, { useState } from 'react'
import Layout from '../../../components/layout/Layout'
import { ChevronLeftIcon, RefreshIcon } from '@heroicons/react/solid'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import usePut from '../../../hooks/usePut'
import useRead from '../../../hooks/useRead'
import spinner from '../../../assets/images/spinner.gif'
import toast, { Toaster } from 'react-hot-toast'

const EmployeeSalaryDetails = () => {
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const navigate = useNavigate()

    // New Scheme is the only active scheme.
    // const schemeType = searchParams.get('scheme') || 'old'
    const schemeType = 'new'
    const salaryType = searchParams.get('type') || 'current_type'
    const targetSalaryId = searchParams.get('salary_id')

    const initialSalaryState = {
        basic_monthly: '',
        basic_yearly: '',
        hra_monthly: '',
        hra_yearly: '',
        conveyance_monthly: '',
        conveyance_yearly: '',
        other_allowance_monthly: '',
        other_allowance_yearly: '',
        stat_bonus_monthly: '',
        stat_bonus_yearly: '',
        employer_epf_monthly: '',
        employer_epf_yearly: '',
        variable_fixed_monthly: '',
        variable_fixed_yearly: '',
        da_monthly: '',
        da_yearly: '',
        washing_allowance_monthly: '',
        washing_allowance_yearly: '',
        supplementary_allowance_monthly: '',
        supplementary_allowance_yearly: '',
        travel_allowance_monthly: '',
        travel_allowance_yearly: '',
        monthly_ctc: '',
        annual_ctc: '',
        monthly_gross: '',
        annual_gross: '',
        is_bonus_eligible: false,
        from_date: new Date().toISOString().split('T')[0],
        previous_year: false,
    }

    const [salaryForm, setSalaryForm] = useState(initialSalaryState)

    const { list } = useRead({
        url:
            salaryType === 'previous_type'
                ? 'getEmployeeSalary'
                : 'getEmployeeCurrentSalary',
        initialData: {
            employee_id: id,
            scheme_type: schemeType,
        },
        onSuccess(res) {
            const rawData =
                salaryType === 'previous_type'
                    ? res?.data?.find((s) => s.salary_id === targetSalaryId)
                    : res?.data?.[0]

            if (rawData) {
                setSalaryForm({
                    ...initialSalaryState,
                    ...rawData,
                    is_bonus_eligible: Boolean(rawData.is_bonus_eligible),
                    from_date: rawData.from_date
                        ? new Date(rawData.from_date * 1000).toISOString().split('T')[0]
                        : new Date().toISOString().split('T')[0],
                })
            }
        },
    })

    const handleChangeDateDetail = (e) => {
        let value = e.target.value
        let name = e.target.name
        setSalaryForm((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const getNumericValue = (val) => (val === '' || isNaN(+val) ? 0 : +val)

    // Frontend dynamic calculation for Total incl. and Total Excl.
    const totalInclusiveMonthly =
        getNumericValue(salaryForm.basic_monthly) +
        getNumericValue(salaryForm.da_monthly)
    const totalInclusiveYearly = totalInclusiveMonthly * 12

    const totalExclusiveMonthly =
        getNumericValue(salaryForm.hra_monthly) +
        getNumericValue(salaryForm.stat_bonus_monthly) +
        getNumericValue(salaryForm.employer_epf_monthly)
    const totalExclusiveYearly = totalExclusiveMonthly * 12

    const calculateSalary = () => {
        let calculatedSalary = {
            ...salaryForm,
        }

        if (schemeType === 'new') {
            const basicM = getNumericValue(salaryForm.basic_monthly)
            const daM = getNumericValue(salaryForm.da_monthly)
            const hraM = getNumericValue(salaryForm.hra_monthly)
            const statBonusM = getNumericValue(salaryForm.stat_bonus_monthly)
            const employerEpfM = getNumericValue(salaryForm.employer_epf_monthly)
            // Legacy new scheme fields kept for backward compatibility
            const conveyanceM = getNumericValue(salaryForm.conveyance_monthly)
            const otherM = getNumericValue(salaryForm.other_allowance_monthly)
            const variableFixedM = getNumericValue(salaryForm.variable_fixed_monthly)

            const totalInclusiveM = basicM + daM
            const totalExclusiveM = hraM + statBonusM + employerEpfM
            const monthlyGross =
                totalInclusiveM + totalExclusiveM + conveyanceM + otherM + variableFixedM

            calculatedSalary = {
                ...calculatedSalary,
                basic_yearly: basicM * 12,
                da_yearly: daM * 12,
                hra_yearly: hraM * 12,
                stat_bonus_yearly: statBonusM * 12,
                employer_epf_yearly: employerEpfM * 12,
                conveyance_yearly: conveyanceM * 12,
                other_allowance_yearly: otherM * 12,
                variable_fixed_yearly: variableFixedM * 12,
                monthly_gross: monthlyGross,
                annual_gross: monthlyGross * 12,
                monthly_ctc: salaryForm.monthly_ctc !== '' ? salaryForm.monthly_ctc : monthlyGross,
                annual_ctc:
                    (salaryForm.monthly_ctc !== ''
                        ? getNumericValue(salaryForm.monthly_ctc)
                        : monthlyGross) * 12,
            }
        } else if (salaryForm.previous_year) {
            // Previous Year calculation (Old Scheme)
            const basicM = getNumericValue(salaryForm.basic_monthly)
            const hraM = getNumericValue(salaryForm.hra_monthly)
            const conveyanceM = getNumericValue(salaryForm.conveyance_monthly)
            const otherM = getNumericValue(salaryForm.other_allowance_monthly)
            const variableFixedM = getNumericValue(salaryForm.variable_fixed_monthly)

            const monthlyGross = basicM + hraM + conveyanceM + otherM + variableFixedM

            calculatedSalary = {
                ...calculatedSalary,
                basic_yearly: basicM * 12,
                hra_yearly: hraM * 12,
                conveyance_yearly: conveyanceM * 12,
                other_allowance_yearly: otherM * 12,
                variable_fixed_yearly: variableFixedM * 12,
                monthly_gross: monthlyGross,
                annual_gross: monthlyGross * 12,
            }
        } else {
            // Current Year calculation (Old Scheme)
            const basicM = getNumericValue(salaryForm.basic_monthly)
            const hraM = getNumericValue(salaryForm.hra_monthly)
            const daM = getNumericValue(salaryForm.da_monthly)
            const saM = getNumericValue(salaryForm.supplementary_allowance_monthly)
            const taM = getNumericValue(salaryForm.travel_allowance_monthly)
            const waM = getNumericValue(salaryForm.washing_allowance_monthly)
            const variableFixedM = getNumericValue(salaryForm.variable_fixed_monthly)

            const monthlyGross =
                basicM + hraM + daM + saM + taM + waM + variableFixedM

            calculatedSalary = {
                ...calculatedSalary,
                basic_yearly: basicM * 12,
                hra_yearly: hraM * 12,
                da_yearly: daM * 12,
                supplementary_allowance_yearly: saM * 12,
                travel_allowance_yearly: taM * 12,
                washing_allowance_yearly: waM * 12,
                variable_fixed_yearly: variableFixedM * 12,
                monthly_gross: monthlyGross,
                annual_gross: monthlyGross * 12,
            }

            if (salaryForm.monthly_ctc !== '') {
                calculatedSalary.annual_ctc = getNumericValue(salaryForm.monthly_ctc) * 12
            }
        }

        setSalaryForm({ ...calculatedSalary })
    }

    const handleChangeCents = (e) => {
        let value = e.target.value === '' ? '' : +e.target.value
        let name = e.target.name
        setSalaryForm((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const calculateData = () => {
        calculateSalary()
    }

    const { handleAuth, isLoading: createLoading } = usePut({
        url: 'createEmployeeSalary',
        onSuccess: () => {
            navigate(`/employee-salary-details/${id}?scheme=${schemeType}`)
        },
    })

    const updateEmployeeSalary = usePut({
        url: 'updateEmployeeSalary',
        refreshUrl: '/getEmployeeCurrentSalary',
        onSuccess: () => {
            toast.success('Salary Updated Successfully')
        },
        onError: () => {
            toast.error('There was an error while updating salary')
        },
    })

    const handleSaveSalary = () => {
        let annualCTC = 0

        if (schemeType === 'new') {
            annualCTC =
                (getNumericValue(salaryForm.basic_monthly) +
                    getNumericValue(salaryForm.da_monthly) +
                    getNumericValue(salaryForm.hra_monthly) +
                    getNumericValue(salaryForm.stat_bonus_monthly) +
                    getNumericValue(salaryForm.employer_epf_monthly) +
                    getNumericValue(salaryForm.conveyance_monthly) +
                    getNumericValue(salaryForm.other_allowance_monthly) +
                    getNumericValue(salaryForm.variable_fixed_monthly)) *
                12
        } else if (salaryForm.previous_year) {
            annualCTC =
                salaryForm.basic_yearly +
                salaryForm.hra_yearly +
                salaryForm.conveyance_yearly +
                salaryForm.other_allowance_yearly +
                salaryForm.variable_fixed_yearly
        } else {
            annualCTC =
                salaryForm.basic_yearly +
                salaryForm.hra_yearly +
                salaryForm.da_yearly +
                salaryForm.supplementary_allowance_yearly +
                salaryForm.travel_allowance_yearly +
                salaryForm.washing_allowance_yearly +
                salaryForm.variable_fixed_yearly
        }

        if (!salaryForm.from_date || !annualCTC) {
            return alert(
                'Please Enter All The Details: "ANNUAL CTC" and "EFFECT FROM DATE" '
            )
        }

        let data = {
            ...salaryForm,
            salary_id: salaryForm.salary_id || null,
            employee_id: id,
            scheme_type: schemeType,
            annual_ctc: annualCTC,
            basic_percentage: salaryForm.basic_ctc_cent,
            hra_percentage: salaryForm.hra_cent,
            is_bonus_eligible: Boolean(salaryForm.is_bonus_eligible),
            from_date: Math.floor(new Date(salaryForm.from_date).getTime() / 1000),
        }

        handleAuth.mutate(data)
    }

    const handleUpdateSalary = () => {
        let fromDateTimestamp = salaryForm.from_date
        if (typeof salaryForm.from_date === 'string' && salaryForm.from_date.includes('-')) {
            fromDateTimestamp = Math.floor(new Date(salaryForm.from_date).getTime() / 1000)
        }

        let annualCTC = 0
        if (schemeType === 'new') {
            annualCTC =
                (getNumericValue(salaryForm.basic_monthly) +
                    getNumericValue(salaryForm.da_monthly) +
                    getNumericValue(salaryForm.hra_monthly) +
                    getNumericValue(salaryForm.stat_bonus_monthly) +
                    getNumericValue(salaryForm.employer_epf_monthly) +
                    getNumericValue(salaryForm.conveyance_monthly) +
                    getNumericValue(salaryForm.other_allowance_monthly) +
                    getNumericValue(salaryForm.variable_fixed_monthly)) *
                12
        } else if (salaryForm.previous_year) {
            annualCTC =
                salaryForm.basic_yearly +
                salaryForm.hra_yearly +
                salaryForm.conveyance_yearly +
                salaryForm.other_allowance_yearly +
                salaryForm.variable_fixed_yearly
        } else {
            annualCTC =
                salaryForm.basic_yearly +
                salaryForm.hra_yearly +
                salaryForm.da_yearly +
                salaryForm.supplementary_allowance_yearly +
                salaryForm.travel_allowance_yearly +
                salaryForm.washing_allowance_yearly +
                salaryForm.variable_fixed_yearly
        }

        let data = {
            ...salaryForm,
            salary_id: salaryForm.salary_id || targetSalaryId,
            employee_id: id,
            scheme_type: schemeType,
            annual_ctc: annualCTC || salaryForm.annual_ctc,
            is_bonus_eligible: Boolean(salaryForm.is_bonus_eligible),
            from_date: fromDateTimestamp,
        }
        updateEmployeeSalary.handleAuth.mutate(data)
    }

    return (
        <Layout>
            <Toaster />
            {(handleAuth.isLoading || updateEmployeeSalary.handleAuth.isLoading || list.isLoading) && (
                <div className="fixed top-0 left-0 z-50 w-full h-full bg-[#ffffffa1] flex items-center justify-center">
                    <img src={spinner} className="w-14 h-14 mx-auto" alt="loading" />
                </div>
            )}
            <div className="px-4 py-4 flex justify-between sticky bg-white top-16 shadow-lg z-40">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1, { replace: true })}
                        className="flex items-center hover:underline"
                    >
                        <ChevronLeftIcon className="w-6 h-6" />
                        <span className="font-semibold">Back</span>
                    </button>
                    <h2 className="text-2xl font-semibold">Salary Details</h2>
                    {/* Scheme badge disabled - New Scheme is the only active scheme
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-semibold text-gray-700 uppercase">
                        {schemeType} Scheme
                    </span>
                    */}
                </div>
                {searchParams.get('mode') !== 'resigned' && (
                    <div className="flex items-center space-x-4">
                        {salaryForm.salary_id || targetSalaryId || salaryType === 'current_type' || salaryType === 'previous_type' ? (
                            <button
                                onClick={handleUpdateSalary}
                                disabled={updateEmployeeSalary.handleAuth.isLoading || list.isLoading}
                                className={`px-5 py-2 bg-yellow-300 rounded font-semibold flex items-center space-x-2 ${
                                    updateEmployeeSalary.handleAuth.isLoading || list.isLoading
                                        ? 'opacity-70 cursor-not-allowed'
                                        : ''
                                }`}
                            >
                                {(updateEmployeeSalary.handleAuth.isLoading || list.isLoading) && (
                                    <RefreshIcon className="w-5 h-5 animate-spin" />
                                )}
                                <span>Update Details</span>
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleSaveSalary}
                                    disabled={createLoading || list.isLoading}
                                    className={`px-5 py-2 bg-yellow-300 rounded font-semibold flex items-center space-x-2 ${
                                        createLoading || list.isLoading
                                            ? 'opacity-70 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    {(createLoading || list.isLoading) && (
                                        <RefreshIcon className="w-5 h-5 animate-spin" />
                                    )}
                                    <span>Save Details</span>
                                </button>
                                <Link
                                    to={`/employee-salary-details/${id}?scheme=${schemeType}`}
                                    className="px-5 py-2 bg-blue-100 rounded font-semibold inline-block"
                                >
                                    View Salary
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="px-4 my-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="w-full sm:w-auto">
                        <label htmlFor="from_date" className="label">
                            Effect From:<span className="mandatory">*</span>
                        </label>
                        <div>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="date"
                                    name="from_date"
                                    disabled={searchParams.get('mode') === 'resigned'}
                                    className="input"
                                    value={salaryForm.from_date}
                                    onChange={handleChangeDateDetail}
                                />
                            </div>
                        </div>
                    </div>

                    {/* {schemeType === 'new' && (
                        <div className="flex items-center space-x-2 sm:mt-6">
                            <input
                                type="checkbox"
                                id="is_bonus_eligible"
                                name="is_bonus_eligible"
                                disabled={searchParams.get('mode') === 'resigned'}
                                checked={Boolean(salaryForm.is_bonus_eligible)}
                                onChange={(e) =>
                                    setSalaryForm((prev) => ({
                                        ...prev,
                                        is_bonus_eligible: e.target.checked,
                                    }))
                                }
                                className="w-4 h-4 text-color1 rounded focus:ring-0 cursor-pointer"
                            />
                            <label
                                htmlFor="is_bonus_eligible"
                                className="font-semibold text-gray-700 cursor-pointer select-none text-sm"
                            >
                                Bonus Eligible
                            </label>
                        </div>
                    )} */}
                </div>

                <div className="px-10 border border-gray-200 mt-10">
                    <div className="grid grid-cols-8 gap-x-8 border-b border-gray-200 py-4">
                        <div className="col-span-2">
                            <label className="label">SALARY COMPONENTS:</label>
                        </div>
                        <div className="col-span-2">
                            <label className="label">MONTHLY AMOUNT:</label>
                        </div>
                        <div className="col-span-2 text-center">
                            <label className="label">ANNUAL AMOUNT:</label>
                        </div>
                    </div>
                    <div className="space-y-8 pb-8">
                        <div className="mt-5">
                            <h2 className="font-semibold text-xl">Earnings</h2>
                        </div>

                        {/* Basic */}
                        <div className="grid grid-cols-8 mt-5 gap-x-8">
                            <div className="col-span-2">
                                <label className="label">Basic:</label>
                            </div>
                            <div className="col-span-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        onWheel={(e) => e.target.blur()}
                                        type="number"
                                        disabled={searchParams.get('mode') === 'resigned'}
                                        name="basic_monthly"
                                        value={salaryForm.basic_monthly}
                                        onBlur={calculateData}
                                        onChange={handleChangeCents}
                                        className="input"
                                        placeholder=""
                                    />
                                </div>
                            </div>
                            <div className="col-span-2 text-center">
                                <label className="label bg-gray-100 input h-[2.6rem]">
                                    {salaryForm?.basic_yearly}
                                </label>
                            </div>
                        </div>

                        {schemeType === 'new' ? (
                            // New Scheme Fields (matches Payout Wages table)
                            <>
                                {/* DA */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">DA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="da_monthly"
                                                value={salaryForm.da_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.da_yearly}
                                        </label>
                                    </div>
                                </div>

                                {/* Total incl. */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8 bg-gray-50 py-2 rounded -mx-2 px-2">
                                    <div className="col-span-2 flex items-center">
                                        <label className="label font-bold text-gray-800">
                                            Total incl.:
                                        </label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="label bg-gray-200 font-bold input h-[2.6rem] flex items-center">
                                            &#8377; {totalInclusiveMonthly}
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <div className="label bg-gray-200 font-bold input h-[2.6rem] flex items-center justify-center">
                                            &#8377; {totalInclusiveYearly}
                                        </div>
                                    </div>
                                </div>

                                {/* HRA */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">HRA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="hra_monthly"
                                                value={salaryForm.hra_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.hra_yearly}
                                        </label>
                                    </div>
                                </div>

                                {/* Stat Bonus */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Stat Bonus:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="stat_bonus_monthly"
                                            value={salaryForm.stat_bonus_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.stat_bonus_yearly}
                                        </label>
                                    </div>
                                </div>

                                {/* Employer PF */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Employer PF:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="employer_epf_monthly"
                                            value={salaryForm.employer_epf_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.employer_epf_yearly}
                                        </label>
                                    </div>
                                </div>

                                {/* Total Excl. */}
                                <div className="grid grid-cols-8 mt-5 gap-x-8 bg-gray-50 py-2 rounded -mx-2 px-2">
                                    <div className="col-span-2 flex items-center">
                                        <label className="label font-bold text-gray-800">
                                            Total Excl.:
                                        </label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="label bg-gray-200 font-bold input h-[2.6rem] flex items-center">
                                            &#8377; {totalExclusiveMonthly}
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <div className="label bg-gray-200 font-bold input h-[2.6rem] flex items-center justify-center">
                                            &#8377; {totalExclusiveYearly}
                                        </div>
                                    </div>
                                </div>

                                {/* Legacy new scheme fields kept commented out to avoid code deletion */}
                                {/*
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Conveyance:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="conveyance_monthly"
                                                value={salaryForm.conveyance_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.conveyance_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Other Allowance:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="other_allowance_monthly"
                                            value={salaryForm.other_allowance_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.other_allowance_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Variable / Fixed:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="variable_fixed_monthly"
                                            value={salaryForm.variable_fixed_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm?.variable_fixed_yearly}
                                        </label>
                                    </div>
                                </div>
                                */}
                            </>
                        ) : salaryForm.previous_year ? (
                            // Old Scheme Previous Year Fields
                            <>
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">OA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="other_allowance_monthly"
                                            value={salaryForm.other_allowance_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.other_allowance_yearly}
                                        </label>
                                    </div>
                                </div>
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">HRA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="hra_monthly"
                                                value={salaryForm.hra_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.hra_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">CONV:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="conveyance_monthly"
                                                value={salaryForm.conveyance_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.conveyance_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Variable OT:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="variable_fixed_monthly"
                                            value={salaryForm.variable_fixed_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.variable_fixed_yearly}
                                        </label>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // Old Scheme Current Year Fields
                            <>
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">DA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="da_monthly"
                                                value={salaryForm.da_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.da_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">HRA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="hra_monthly"
                                                value={salaryForm.hra_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.hra_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">TA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="travel_allowance_monthly"
                                            value={salaryForm.travel_allowance_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.travel_allowance_yearly}
                                        </label>
                                    </div>
                                </div>
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">WA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="washing_allowance_monthly"
                                            value={salaryForm.washing_allowance_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.washing_allowance_yearly}
                                        </label>
                                    </div>
                                </div>
                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">SA:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                disabled={searchParams.get('mode') === 'resigned'}
                                                name="supplementary_allowance_monthly"
                                                value={salaryForm.supplementary_allowance_monthly}
                                                onBlur={calculateData}
                                                onChange={handleChangeCents}
                                                className="input"
                                                placeholder=""
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.supplementary_allowance_yearly}
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-8 mt-5 gap-x-8">
                                    <div className="col-span-2">
                                        <label className="label">Variable OT:</label>
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            onWheel={(e) => e.target.blur()}
                                            type="number"
                                            disabled={searchParams.get('mode') === 'resigned'}
                                            name="variable_fixed_monthly"
                                            value={salaryForm.variable_fixed_monthly}
                                            onBlur={calculateData}
                                            onChange={handleChangeCents}
                                            className="input"
                                            placeholder=""
                                        />
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <label className="label bg-gray-100 input h-[2.6rem]">
                                            {salaryForm.variable_fixed_yearly}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="grid grid-cols-8 mt-5 gap-x-8 bg-blue-100 rounded -mx-4 px-4 py-2">
                            <div className="col-span-2">
                                <label className="label text-2xl font-semibold">Gross Salary</label>
                            </div>
                            <div className="col-span-2">
                                <label className="label text-xl">
                                    &#8377; {salaryForm.monthly_gross || 0}
                                </label>
                            </div>
                            <div className="col-span-2 text-center">
                                <label className="label text-xl">
                                    &#8377; {salaryForm.annual_gross || 0}
                                </label>
                            </div>
                        </div>

                        {/* CTC visible if not previous year */}
                        {!salaryForm.previous_year && (
                            <div className="grid grid-cols-8 mt-5 gap-x-8 bg-green-100 rounded -mx-4 px-4 py-2">
                                <div className="col-span-2">
                                    <label className="label text-2xl font-semibold">
                                        Cost to Company
                                    </label>
                                </div>
                                <div className="col-span-2">
                                    <input
                                        onWheel={(e) => e.target.blur()}
                                        type="number"
                                        disabled={searchParams.get('mode') === 'resigned'}
                                        name="monthly_ctc"
                                        value={salaryForm.monthly_ctc}
                                        onBlur={calculateData}
                                        onChange={handleChangeCents}
                                        className="input bg-white text-xl"
                                        placeholder=""
                                    />
                                </div>
                                <div className="col-span-2 text-center">
                                    <label className="label bg-white input h-[2.6rem] text-xl">
                                        &#8377; {salaryForm.annual_ctc}
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default EmployeeSalaryDetails